// controllers/passkeyCreateRemoveController.js
import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import toast from "../utils/toast";
import { getUserFriendlyErrorMessage } from "../utils/errors";
import { handleBackendResponse } from "../utils/backendResponseHandler";  
import { handleDetectPasskeyMetadata } from "../utils/browserDetectionHandler";
import { rrFetch } from "../utils/rr_fetch";
import { debugLog } from "../utils";
import { debugError } from "../utils";

export default class extends Controller {
  static targets = ["form", "spinner", "error", "result", "button", "buttonText", "setupButton", "skipButton", "setupSpinner", "setupIcon", "setupButtonText", "pinButton"];
  static values = { 
    initUrl: { type: String, default: "/passkey/init" },
    domain: String,
    company: String,
    payload: Object,
    idempotencyKey: String,
    csrfToken: String,
  };

  connect() {
    debugLog("Passkey create remove controller connected");
  }

  // create passkey from security page
  async createPasskeyFromSecurity(event) {
    event.preventDefault();
    
    // Prevent double-click
    if (this.buttonTarget.disabled) {
      return;
    }
    
    this.showSpinner();
    this.disableCreatePasskeyButton();
    this.disablePinButton();
    
    try {
      await this.requestChallengeFromSecurity();
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      this.hideSpinner();
      this.enableCreatePasskeyButton();
      this.enablePinButton();
    }
  }
  
  // create passkey from nag passkey modal
  async createPasskeyFromSend(event) {
    event.preventDefault();

    // Prevent double-click
    if (this.hasSetupButtonTarget && this.setupButtonTarget.disabled) {
      return;
    }

    this.disableSkipButton();
    this.disableSetupButton();
    
    try {
      await this.requestChallengeFromSend();
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      
      this.enableSkipButton();
      this.enableSetupButton();
    }
  }

  // create passkey from nag passkey modal for withdraw-to-crypto
  async createPasskeyFromWithdrawToCrypto(event) {
    event.preventDefault();

    // Prevent double-click
    if (this.hasSetupButtonTarget && this.setupButtonTarget.disabled) {
      return;
    }

    this.disableSkipButton();
    this.disableSetupButton();
    
    try {
      await this.requestChallengeFromWithdrawToCrypto();
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      
      this.enableSkipButton();
      this.enableSetupButton();
    }
  }

    // create passkey from nag passkey modal for withdraw-to-crypto
  async createPasskeyFromWithdraw(event) {
    event.preventDefault();

    // Prevent double-click
    if (this.hasSetupButtonTarget && this.setupButtonTarget.disabled) {
      return;
    }

    this.disableSkipButton();
    this.disableSetupButton();
    
    try {
      await this.requestChallengeFromWithdraw();
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      
      this.enableSkipButton();
      this.enableSetupButton();
    }
  }

  async requestChallengeFromSecurity() {
    // Step 1: Get challenge from backend
    const challenge = await this.getChallenge();
  
    // Step 2: Create passkey using WebAuthn
    const attestation = await this.createWebAuthnCredential(challenge);
  
    // Step 3: Save passkey to backend (handles toasts internally)
    const response = await this.savePasskey(attestation);
  
    return response;
  }

  async requestChallengeFromSend() {
    // Step 1: Get challenge from backend
    const challenge = await this.getChallenge();
    
    // Step 2: Create passkey using WebAuthn
    const attestation = await this.createWebAuthnCredential(challenge);
    
    // Step 3: Save passkey to backend
    await this.savePasskey(attestation);
  }

  async requestChallengeFromWithdrawToCrypto() {
    // Step 1: Get challenge from backend
    const challenge = await this.getChallenge();
    
    // Step 2: Create passkey using WebAuthn
    const attestation = await this.createWebAuthnCredential(challenge);
    
    // Step 3: Save passkey to backend
    await this.savePasskey(attestation);
  }  

    async requestChallengeFromWithdraw() {
    // Step 1: Get challenge from backend
    const challenge = await this.getChallenge();
    
    // Step 2: Create passkey using WebAuthn
    const attestation = await this.createWebAuthnCredential(challenge);
    
    // Step 3: Save passkey to backend
    await this.savePasskey(attestation);
  }  

  async getChallenge() {
    // Add source as query parameter if payload contains source
    let url = this.initUrlValue;
    if (this.hasPayloadValue && this.payloadValue && this.payloadValue.source) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}source=${encodeURIComponent(this.payloadValue.source)}`;
    }
    
    const response = await rrFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return await handleBackendResponse(response, "Failed to get challenge");
  }

  async createWebAuthnCredential(challengeData) {
    // For WebAuthn, relying party ID must match the current domain
    const currentDomain = window.location.hostname;
    const relyingPartyId = this.domainValue || currentDomain;
    const relyingPartyName = this.companyValue || "CoinSub Wallet";
    
    let webauthn = new WebAuthnSigner({
      relyingParty: {
        id: relyingPartyId,
        name: relyingPartyName,
      },
    });

    const challenge = challengeData.challenge;

    try {
      const attestation = await webauthn.create(challenge);
      
      this.credentialInfoValue = {
          credentialName: "rocket passkey",
          credentialKind: "Fido2",
          credentialInfo: attestation.credentialInfo,
          challengeIdentifier: challenge.challengeIdentifier,
      };

      if (this.credentialInfoValue.credentialInfo !== null,
          this.credentialInfoValue.challengeIdentifier !== null, 
          this.credentialInfoValue.credentialName !== null,
          this.credentialInfoValue.credentialKind !== null) {
              return this.credentialInfoValue;
      }
    } catch (error) {
      // Handle "A request is already pending" error
      if (error.name === "InvalidStateError" || error.message?.includes("pending")) {
        toast("A passkey request is already in progress. Please refresh the page and try again.", "error");
        throw new Error("Passkey request already pending");
      }
      throw error;
    }
  }

  async savePasskey(values) {
    debugLog("IDempotency key: ", this.idempotencyKeyValue);
    
    const requestBody = {
      ...values,
      metadata: handleDetectPasskeyMetadata(),
      idempotencyKey: this.idempotencyKeyValue,
    };
    
    // Add source and payload separately if they exist
    if (this.hasPayloadValue && this.payloadValue) {
      requestBody.source = this.payloadValue.source;
      requestBody.payload = this.payloadValue;
    } else {
      // For security flow, set source to "security"
      requestBody.source = "security";
    }
    
    const response = await rrFetch('/passkey/complete', {
      method: "POST",
      body: JSON.stringify(requestBody),
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
    });

    return await handleBackendResponse(response, "Failed to save passkey");
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  disableCreatePasskeyButton() {
    this.buttonTarget.disabled = true;
  }

  enableCreatePasskeyButton() {
    this.buttonTarget.disabled = false;
  }

  disableSkipButton() {
    this.skipButtonTarget.disabled = true;
  }

  enableSkipButton() {
    this.skipButtonTarget.disabled = false;
  }

  disableSetupButton() {
    if (this.hasSetupButtonTarget) {
      this.setupButtonTarget.disabled = true;
    }
    
    // Show spinner, hide icon
    if (this.hasSetupSpinnerTarget) {
      this.setupSpinnerTarget.classList.remove('hidden');
    }
    if (this.hasSetupIconTarget) {
      this.setupIconTarget.classList.add('hidden');
    }
    
    // Update button text
    if (this.hasSetupButtonTextTarget) {
      this.setupButtonTextTarget.textContent = "Setting up...";
    }
  }

  enableSetupButton() {
    if (this.hasSetupButtonTarget) {
      this.setupButtonTarget.disabled = false;
    }
    
    // Hide spinner, show icon
    if (this.hasSetupSpinnerTarget) {
      this.setupSpinnerTarget.classList.add('hidden');
    }
    if (this.hasSetupIconTarget) {
      this.setupIconTarget.classList.remove('hidden');
    }
    
    // Restore button text
    if (this.hasSetupButtonTextTarget) {
      this.setupButtonTextTarget.textContent = "Setup Passkey";
    }
  }

  disablePinButton() {
    if (this.hasPinButtonTarget) {
      this.pinButtonTarget.classList.add('pointer-events-none', 'opacity-50', 'cursor-not-allowed');
      this.pinButtonTarget.setAttribute('aria-disabled', 'true');
    }
  }

  enablePinButton() {
    if (this.hasPinButtonTarget) {
      this.pinButtonTarget.classList.remove('pointer-events-none', 'opacity-50', 'cursor-not-allowed');
      this.pinButtonTarget.removeAttribute('aria-disabled');
    }
  }
}
